"use client";

import {
  Button,
  FormControl,
  InputLabel,
  Popover,
  MenuItem,
  Select,
  Box,
  SelectChangeEvent,
} from "@mui/material";
import { Calendar } from "lucide-react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useState, useMemo, useEffect, MouseEvent } from "react";
import dayjs, { Dayjs, ManipulateType, OpUnitType } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import useTimezoneStore from "@/stores/timezone.store";
import useFilterStore from "@/stores/order-filter.store";
import { dateFilterOptions } from "@/constants/date-filter-options";

const PURPLE = {
  base: "#9762b0",
  hover: "#7f4f96",
  focus: "#d8b4fe",
  lightBg: "#faf5ff",
};

const DatePickerBox = ({
  fields,
  defaultValues,
}: {
  fields: [string, string];
  defaultValues: [Date, Date];
}) => {
  const filter = useFilterStore((s) => s.filter);
  const updateFilter = useFilterStore((s) => s.updateFilter);
  const displayTimeZone = useTimezoneStore((s) => s.timezone);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [keyOption, setKeyOption] = useState(
    () => dateFilterOptions.find((o) => o.isDefault)?.key || "custom",
  );
  const [isDateRangeError, setDateRangeError] = useState(false);

  const [tempDate, setTempDate] = useState<{
    dateFrom: Dayjs | null;
    dateTo: Dayjs | null;
  }>({
    dateFrom: null,
    dateTo: null,
  });

  const valueFromStore = useMemo(() => {
    const startDateString = filter[fields[0]] as string;
    const endDateString = filter[fields[1]] as string;

    if (startDateString && endDateString) {
      return {
        dateFrom: dayjs(startDateString).tz(displayTimeZone),
        dateTo: dayjs(endDateString).tz(displayTimeZone),
      };
    }

    return {
      dateFrom: dayjs(defaultValues[0]).tz(displayTimeZone),
      dateTo: dayjs(defaultValues[1]).tz(displayTimeZone),
    };
  }, [filter, fields, defaultValues, displayTimeZone]);

  useEffect(() => {
    const startDateString = filter[fields[0]] as string;
    const endDateString = filter[fields[1]] as string;

    if (!startDateString && !endDateString) {
      updateFilter({
        [fields[0]]: dayjs(defaultValues[0]).tz(displayTimeZone).toISOString(),
        [fields[1]]: dayjs(defaultValues[1]).tz(displayTimeZone).toISOString(),
      });
    }
  }, [fields, defaultValues, filter, updateFilter, displayTimeZone]);

  useEffect(() => {
    const option = dateFilterOptions.find((o) => o.key === keyOption);
    if (!option || !option.manipulate) return;

    const newDates = {
      dateFrom: dayjs().tz(displayTimeZone),
      dateTo: dayjs().tz(displayTimeZone),
    };

    if (option.manipulate.type === "duringInDate") {
      const [value, unit] = option.manipulate.add as [number, ManipulateType];
      const targetDate = dayjs().tz(displayTimeZone).add(value, unit);
      newDates.dateFrom = targetDate.startOf(
        option.manipulate.startOf as OpUnitType,
      );
      newDates.dateTo = targetDate.endOf(option.manipulate.endOf as OpUnitType);
    } else if (option.manipulate.type === "dayToDay") {
      const [valueStart, unitStart] = option.manipulate.addStart as [
        number,
        ManipulateType,
      ];
      const [valueEnd, unitEnd] = option.manipulate.addEnd as [
        number,
        ManipulateType,
      ];
      newDates.dateFrom = dayjs()
        .tz(displayTimeZone)
        .add(valueStart, unitStart)
        .startOf("day");
      newDates.dateTo = dayjs()
        .tz(displayTimeZone)
        .add(valueEnd, unitEnd)
        .subtract(1, "day")
        .endOf("day");
    }

    updateFilter({
      [fields[0]]: newDates.dateFrom.toISOString(),
      [fields[1]]: newDates.dateTo.toISOString(),
    });
    handleClose();
  }, [keyOption, displayTimeZone, fields, updateFilter]);

  const handleClickOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setTempDate(valueFromStore);
    setDateRangeError(false);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleOK = () => {
    const { dateFrom, dateTo } = tempDate;
    if (dateFrom && dateTo && dateFrom.isAfter(dateTo)) {
      setDateRangeError(true);
      return;
    }

    updateFilter({
      [fields[0]]: dateFrom ? dateFrom.startOf("day").toISOString() : null,
      [fields[1]]: dateTo ? dateTo.endOf("day").toISOString() : null,
    });
    setDateRangeError(false);
    handleClose();
  };

  const handleChangeOption = (event: SelectChangeEvent) => {
    setKeyOption(event.target.value as string);
  };

  const isCustomRange = keyOption === "custom";
  const open = Boolean(anchorEl);

  const buttonLabel = useMemo(() => {
    const selectedOption = dateFilterOptions.find((o) => o.key === keyOption);
    if (keyOption !== "custom" && selectedOption) {
      return selectedOption.label;
    }

    const { dateFrom, dateTo } = valueFromStore;
    if (dateFrom && dateTo) {
      return `${dateFrom.format("MMM DD, YYYY")} to ${dateTo.format("MMM DD, YYYY")}`;
    }

    return "Select Date Range";
  }, [keyOption, valueFromStore]);

  return (
    <>
      <Button
        variant="outlined"
        endIcon={<Calendar className="text-gray-600" />}
        onClick={handleClickOpen}
        sx={{
          height: "50px",
          color: "#4a5565",
          background: "#fff",
          border: "1px solid #d1d5db",
          borderRadius: "6px",
          fontSize: "1rem",
          outlineColor: "#0000",
          transition:
            "background-color .2s, color .2s, border-color .2s, box-shadow .2s",
          "&:hover": {
            borderColor: PURPLE.base,
          },
          textTransform: "none",
        }}
      >
        {buttonLabel}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box p={2} width="auto">
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel
              sx={{
                "&.Mui-focused": {
                  color: PURPLE.base,
                },
              }}
            >
              Date Range
            </InputLabel>
            <Select
              label="Date Range"
              value={keyOption}
              onChange={handleChangeOption}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d1d5db",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: PURPLE.base,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: PURPLE.base,
                },
                "& .MuiSelect-icon": {
                  color: PURPLE.base,
                },
              }}
            >
              {dateFilterOptions.map((option) => (
                <MenuItem value={option.key} key={option.key}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <DatePicker
                label="Start Date"
                value={tempDate.dateFrom}
                onChange={(newValue) =>
                  setTempDate((d) => ({ ...d, dateFrom: newValue }))
                }
                timezone={displayTimeZone}
                disabled={!isCustomRange}
                maxDate={tempDate.dateTo || undefined}
                slotProps={{
                  textField: {
                    size: "small",
                    error: isDateRangeError,
                    helperText: isDateRangeError ? "Start date invalid" : "",
                    sx: {
                      "& .MuiPickersOutlinedInput-root": {
                        "& .MuiPickersOutlinedInput-notchedOutline": {
                          borderColor: "#d1d5db",
                        },
                        "&:hover .MuiPickersOutlinedInput-notchedOutline": {
                          borderColor: PURPLE.base,
                        },
                        "&.Mui-focused .MuiPickersOutlinedInput-notchedOutline":
                          {
                            borderColor: `${PURPLE.base} !important`,
                          },
                      },
                      "& .MuiInputLabel-root": {
                        "&.Mui-focused": {
                          color: PURPLE.base,
                        },
                      },
                    },
                  },
                  popper: {
                    sx: {
                      "& .MuiPickersDay-root.Mui-selected": {
                        backgroundColor: `${PURPLE.base} !important`,
                        color: "#ffffff !important",
                        "&:hover": {
                          backgroundColor: `${PURPLE.hover} !important`,
                        },
                      },
                      "& .MuiPickersDay-root.Mui-selected.Mui-focusVisible": {
                        backgroundColor: `${PURPLE.base} !important`,
                      },
                    },
                  },
                }}
              />
              <DatePicker
                label="End Date"
                value={tempDate.dateTo}
                onChange={(newValue) =>
                  setTempDate((d) => ({ ...d, dateTo: newValue }))
                }
                timezone={displayTimeZone}
                disabled={!isCustomRange}
                minDate={tempDate.dateFrom || undefined}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      "& .MuiPickersOutlinedInput-root": {
                        "& .MuiPickersOutlinedInput-notchedOutline": {
                          borderColor: "#d1d5db",
                        },
                        "&:hover .MuiPickersOutlinedInput-notchedOutline": {
                          borderColor: PURPLE.base,
                        },
                        "&.Mui-focused .MuiPickersOutlinedInput-notchedOutline":
                          {
                            borderColor: `${PURPLE.base} !important`,
                          },
                      },
                      "& .MuiInputLabel-root": {
                        "&.Mui-focused": {
                          color: PURPLE.base,
                        },
                      },
                    },
                  },
                  popper: {
                    sx: {
                      "& .MuiPickersDay-root.Mui-selected": {
                        backgroundColor: `${PURPLE.base} !important`,
                        color: "#ffffff !important",
                        "&:hover": {
                          backgroundColor: `${PURPLE.hover} !important`,
                        },
                      },
                      "& .MuiPickersDay-root.Mui-selected.Mui-focusVisible": {
                        backgroundColor: `${PURPLE.base} !important`,
                      },
                    },
                  },
                }}
              />
            </Box>
          </LocalizationProvider>

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}
          >
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{
                borderRadius: "6px",
                color: PURPLE.base,
                borderColor: PURPLE.base,
                "&:hover": {
                  backgroundColor: PURPLE.lightBg,
                  borderColor: PURPLE.hover,
                  color: PURPLE.hover,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleOK}
              disabled={!isCustomRange}
              sx={{
                borderRadius: "6px",
                backgroundColor: PURPLE.base,
                color: "#fff",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: PURPLE.hover,
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  backgroundColor: PURPLE.lightBg,
                  color: PURPLE.focus,
                },
              }}
            >
              OK
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default DatePickerBox;
